<script lang="ts">
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"

  type Props = {
    event?: ScheduleEvent | null
    limit?: number | null
    showTitle?: boolean
    onShowAll?: (() => void) | null
  }

  const { event = null, limit = 3, showTitle = true, onShowAll = null }: Props = $props()

  const clean = (value: unknown): string | null => {
    if (value === null || value === undefined) return null

    const text = String(value).trim()

    return text || null
  }

  const formatDateTime = (value?: string | null) => {
    const text = clean(value)

    if (!text) return null

    const date = new Date(text)

    if (Number.isNaN(date.getTime())) return text

    return date.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const entries = $derived(
    [...(event?.lifecycle_entries ?? [])]
      .filter((entry) => Boolean(entry.presentation))
      .sort((a, b) => {
        const aTime = Date.parse(a.occurred_at)
        const bTime = Date.parse(b.occurred_at)

        return (Number.isFinite(bTime) ? bTime : 0) - (Number.isFinite(aTime) ? aTime : 0)
      }),
  )
  const visibleEntries = $derived(limit === null ? entries : entries.slice(0, Math.max(0, limit)))
  const hasHiddenEntries = $derived(limit !== null && entries.length > visibleEntries.length)
</script>

{#if entries.length}
  <section class="lifecycle-timeline">
    {#if showTitle}
      <div class="lifecycle-timeline__heading">
        <SectionTitle
          small={true}
          title="Termin-Lifecycle"
          style="margin-top: 0.3rem; margin-bottom: 0.2rem;"
        />
        {#if hasHiddenEntries && onShowAll}
          <CTA
            type="tertiaryButton"
            content={`Alle anzeigen (${entries.length})`}
            onClick={() => onShowAll?.()}
          ></CTA>
        {/if}
      </div>
    {/if}
    <ol>
      {#each visibleEntries as entry (entry.id)}
        {#if entry.presentation}
          {@const presentation = entry.presentation}
          <li class={`lifecycle-entry lifecycle-entry--${presentation.tone ?? "neutral"}`}>
            <div class="lifecycle-entry__marker" aria-hidden="true"></div>
            <div class="lifecycle-entry__content">
              <div class="lifecycle-entry__header">
                <strong>{presentation.title}</strong>
                {#if formatDateTime(entry.occurred_at)}
                  <span>{formatDateTime(entry.occurred_at)}</span>
                {/if}
              </div>
              <p>{presentation.description}</p>
              {#if presentation.facts?.length}
                <dl>
                  {#each presentation.facts as fact}
                    <div>
                      <dt>{fact.label}</dt>
                      <dd>{fact.value}</dd>
                    </div>
                  {/each}
                </dl>
              {/if}
            </div>
          </li>
        {/if}
      {/each}
    </ol>
  </section>
{/if}

<style lang="less">
  .lifecycle-timeline {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    color: var(--overlay-text-color, var(--text-100));
  }

  .lifecycle-timeline__heading {
    display: grid;
    grid-template-columns: minmax(0, 1fr) max-content;
    align-items: center;
    gap: 0.75rem;
  }

  ol {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  li {
    display: grid;
    grid-template-columns: 0.85rem minmax(0, 1fr);
    gap: 0.45rem;
    min-width: 0;
  }

  .lifecycle-entry__marker {
    width: 0.5rem;
    height: 0.5rem;
    margin-top: 0.35rem;
    border: 1px solid var(--lifecycle-tone, var(--green-100));
    background: color-mix(in srgb, var(--lifecycle-tone, var(--green-100)) 28%, transparent);
  }

  .lifecycle-entry--success {
    --lifecycle-tone: var(--green-100);
  }

  .lifecycle-entry--warning {
    --lifecycle-tone: var(--orange-100, #e28a00);
  }

  .lifecycle-entry--danger {
    --lifecycle-tone: var(--red-100, #e34040);
  }

  .lifecycle-entry--info {
    --lifecycle-tone: var(--blue-100, #176996);
  }

  .lifecycle-entry--neutral {
    --lifecycle-tone: color-mix(
      in srgb,
      var(--overlay-text-color, var(--text-100)) 42%,
      transparent
    );
  }

  .lifecycle-entry__content {
    min-width: 0;
    border-left: 2px solid color-mix(in srgb, var(--lifecycle-tone) 48%, transparent);
    padding-left: 0.65rem;
  }

  .lifecycle-entry__header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) max-content;
    align-items: baseline;
    gap: 0.75rem;
    min-width: 0;
  }

  .lifecycle-entry__header strong {
    min-width: 0;
    color: var(--overlay-text-color, var(--text-100));
    overflow-wrap: anywhere;
  }

  .lifecycle-entry__header span,
  p,
  dt {
    color: color-mix(in srgb, var(--overlay-text-color, var(--text-100)) 68%, transparent);
  }

  p {
    margin: 0.1rem 0 0;
    line-height: 1.35;
  }

  dl {
    display: flex;
    flex-direction: column;
    gap: 0.12rem;
    margin: 0.25rem 0 0;
  }

  dl div {
    display: grid;
    grid-template-columns: minmax(5rem, max-content) minmax(0, 1fr);
    gap: 0.45rem;
  }

  dt {
    font-family: var(--font-family-bold, "Outfit-Bold", sans-serif);
    font-size: 0.78rem;
    text-transform: uppercase;
  }

  dd {
    min-width: 0;
    margin: 0;
    color: var(--overlay-text-color, var(--text-100));
    overflow-wrap: anywhere;
  }
</style>
